import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Alert, Button, Col, Row } from 'react-bootstrap';

import { useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { SectionTitle } from 'components/UI';
import { isPatientArchived } from 'store/patient/reducer';
import { selectCurrentGid } from 'store/groups/reducer';

import Questionnaire from './Questionnaire';
import AddEditQuestionnaireModal from './AddEditQuestionnaireModal';

interface IQuestionnairesProps {
  questionnaires: ICarePlanQuestionnaire[] | undefined;
  getCarePlan: any;
  carePlanTemplate?: ICarePlanTemplate;
}

const Questionnaires = ({
  questionnaires,
  getCarePlan,
  carePlanTemplate
}: IQuestionnairesProps) => {
  const intl = useIntl();
  const [showAddModal, setShowAddModal] = useState(false);
  const [_questionnaires, setQuestionnaires] = useState(questionnaires || []);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<
    ICarePlanQuestionnaire | undefined
  >(undefined);
  const patient = useAppSelector(({ patient }) => patient.data);
  const patientGid = patient?.subgroup_gid;
  const gid = useAppSelector(selectCurrentGid);
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';
  const _isPatientArchived = useAppSelector(isPatientArchived);
  const mobilePhoneNumber = patient?.MobilePhoneNumber;

  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSelectedQuestionnaire(undefined);
  };

  const handleAddQuestionnaire = () => {
    handleCloseAddModal();
    getCarePlan();
  };

  const onSelectQuestionnaire = (questionnaire: ICarePlanQuestionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setShowAddModal(true);
  };

  const handleEditQuestionnaire = () => {
    getCarePlan();
  };

  const onDeleteQuestionnaire = (id: string) => {
    setQuestionnaires(_questionnaires.filter((elem) => elem.id !== id));
  };

  const [{ data: questionnairesData = [] }] = useAxios<ISurvey[]>(
    {
      url: `/clinician/Questionnaire?group_id=${patientGid || gid}`
    },
    {
      useCache: false
    }
  );

  useEffect(() => {
    setQuestionnaires(questionnaires || []);
  }, [questionnaires]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <SectionTitle
          dataTestId="careplan-questionnaires-title"
          label={intl.formatMessage({ id: 'surveysKey' })}
          pill="SMS"
        />
        {!isTemplateGlobal && (
          <Button
            variant="link"
            size="sm"
            onClick={handleOpenAddModal}
            data-test="careplan-questionnaires-btn"
            disabled={(!!patient && !mobilePhoneNumber) || _isPatientArchived}
          >
            + {intl.formatMessage({ id: 'surveysKey' })}
          </Button>
        )}
      </div>
      {patient && !mobilePhoneNumber && (
        <Alert variant="warning" data-test="careplan-questionnaires-add-sms">
          {intl.formatMessage({ id: 'addPhoneNumberKey' })}
        </Alert>
      )}
      {((patient && mobilePhoneNumber) || !patient) && (
        <Row>
          {_questionnaires && _questionnaires.length > 0 ? (
            _questionnaires.map((questionnaire, index) => (
              <Questionnaire
                dataTestId={`careplan-questionnaire-${index}`}
                key={questionnaire.id}
                questionnaire={questionnaire}
                onDeleteQuestionnaire={onDeleteQuestionnaire}
                handleEdit={handleEditQuestionnaire}
                onShowEditModal={() => onSelectQuestionnaire(questionnaire)}
                questionnairesData={questionnairesData}
                carePlanTemplate={carePlanTemplate}
                disabled={_isPatientArchived}
              />
            ))
          ) : (
            <Col>
              <div
                className="bg-light text-center p-3 font-weight-bolder"
                data-test="careplan-questionnaires-empty"
              >
                {intl.formatMessage({ id: 'noQuestionnairesScheduled' })}
              </div>
            </Col>
          )}
        </Row>
      )}

      {showAddModal && (
        <AddEditQuestionnaireModal
          show={showAddModal}
          handleClose={handleCloseAddModal}
          handleAdd={handleAddQuestionnaire}
          questionnairesData={questionnairesData}
          carePlanTemplate={carePlanTemplate}
          questionnaire={selectedQuestionnaire}
        />
      )}
    </div>
  );
};

export default Questionnaires;
